import React from "react";
import type { VisualStyle } from "@learnengine/shared-types";
import { DEFAULT_VISUAL_STYLE } from "@learnengine/shared-types";
import type { StyleSpec } from "./types";

/*
 * Filter recipes (design-plan/v2/02-style-engine.md §4).
 *
 * IMPORTANT design note: the 273 v1 templates were authored for a DARK surface
 * (light strokes/text on near-black). To re-skin them onto LIGHT (paper) styles
 * without per-template rework, whiteboard/retro luminance-INVERT the main group
 * so bright template content becomes dark ink that reads on paper. This is the
 * cheap, global proof of "free restyle". Full per-template fidelity (accent-role
 * palette + native light-surface colors) is the Phase-1 follow-up; until then
 * `studio` is the guaranteed-correct default.
 */

// ─── Whiteboard: grayscale ink + hand-drawn wobble on paper ──────────────────
const whiteboardDefs = () => (
  <>
    <filter id="anv-whiteboard-ink" x="-6%" y="-6%" width="112%" height="112%" colorInterpolationFilters="sRGB">
      {/* desaturate → invert (light template content becomes dark ink) → boost contrast */}
      <feColorMatrix type="saturate" values="0" result="gray" />
      <feComponentTransfer in="gray" result="inv">
        <feFuncR type="table" tableValues="1 0" />
        <feFuncG type="table" tableValues="1 0" />
        <feFuncB type="table" tableValues="1 0" />
      </feComponentTransfer>
      <feComponentTransfer in="inv" result="ink">
        <feFuncR type="gamma" amplitude="1" exponent="1.5" offset="0" />
        <feFuncG type="gamma" amplitude="1" exponent="1.5" offset="0" />
        <feFuncB type="gamma" amplitude="1" exponent="1.5" offset="0" />
      </feComponentTransfer>
      {/* hand-drawn wobble */}
      <feTurbulence type="fractalNoise" baseFrequency="0.018" numOctaves="2" seed="7" result="noise" />
      <feDisplacementMap in="ink" in2="noise" scale="3.4" xChannelSelector="R" yChannelSelector="G" />
    </filter>
    <pattern id="anv-whiteboard-grid" width="38" height="38" patternUnits="userSpaceOnUse">
      <path d="M 38 0 L 0 0 0 38" fill="none" stroke="#d9d7cb" strokeWidth="0.8" />
    </pattern>
  </>
);
const whiteboardBg = (w: number, h: number) => (
  <g>
    <rect width={w} height={h} fill="#fbfbf6" />
    <rect width={w} height={h} fill="url(#anv-whiteboard-grid)" opacity="0.6" />
  </g>
);

// ─── Watercolour: soft wash + edge bleed on warm paper (keeps hue) ───────────
const watercolourDefs = () => (
  <>
    <filter id="anv-watercolour-wash" x="-10%" y="-10%" width="120%" height="120%" colorInterpolationFilters="sRGB">
      <feGaussianBlur in="SourceGraphic" stdDeviation="0.7" result="soft" />
      <feTurbulence type="fractalNoise" baseFrequency="0.012 0.016" numOctaves="3" seed="11" result="noise" />
      <feDisplacementMap in="soft" in2="noise" scale="6.5" xChannelSelector="R" yChannelSelector="G" result="bleed" />
      <feColorMatrix in="bleed" type="saturate" values="1.2" />
    </filter>
    <filter id="anv-watercolour-paper">
      <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="5" result="grain" />
      <feColorMatrix in="grain" type="matrix"
        values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.05 0" />
    </filter>
  </>
);
const watercolourBg = (w: number, h: number) => (
  <g>
    <rect width={w} height={h} fill="#f4eedd" />
    <rect width={w} height={h} filter="url(#anv-watercolour-paper)" opacity="0.5" />
  </g>
);

// ─── Retro print: posterized duotone (ink on cream) + grain ──────────────────
const retroDefs = () => (
  <>
    <filter id="anv-retro-print" x="-4%" y="-4%" width="108%" height="108%" colorInterpolationFilters="sRGB">
      {/* desaturate → invert → posterize → duotone map (dark→ink, light→cream) */}
      <feColorMatrix type="saturate" values="0" result="gray" />
      <feComponentTransfer in="gray" result="inv">
        <feFuncR type="table" tableValues="1 0" />
        <feFuncG type="table" tableValues="1 0" />
        <feFuncB type="table" tableValues="1 0" />
      </feComponentTransfer>
      <feComponentTransfer in="inv" result="poster">
        <feFuncR type="discrete" tableValues="0 0.5 1" />
        <feFuncG type="discrete" tableValues="0 0.5 1" />
        <feFuncB type="discrete" tableValues="0 0.5 1" />
      </feComponentTransfer>
      <feComponentTransfer in="poster" result="duo">
        <feFuncR type="table" tableValues="0.13 0.95" />
        <feFuncG type="table" tableValues="0.12 0.92" />
        <feFuncB type="table" tableValues="0.22 0.84" />
      </feComponentTransfer>
      {/* grain, masked to painted areas */}
      <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="1" seed="3" result="grain" />
      <feColorMatrix in="grain" type="matrix"
        values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.07 0" result="grainA" />
      <feComposite in="grainA" in2="duo" operator="in" result="grainMasked" />
      <feMerge>
        <feMergeNode in="duo" />
        <feMergeNode in="grainMasked" />
      </feMerge>
    </filter>
  </>
);
const retroBg = (w: number, h: number) => (
  <g>
    <rect width={w} height={h} fill="#f1e9d6" />
    <rect x="10" y="10" width={w - 20} height={h - 20} fill="none" stroke="#cdbfa0" strokeWidth="1.5" opacity="0.5" />
  </g>
);

// ─── Studio (default): the v1 dark look — no filter, guaranteed correct ──────
const studioDefs = () => (
  <pattern id="anv-studio-grid" width="40" height="40" patternUnits="userSpaceOnUse">
    <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#1e293b" strokeWidth="0.5" />
  </pattern>
);
const studioBg = (w: number, h: number) => (
  <g>
    <rect width={w} height={h} fill="#0f172a" />
    <rect width={w} height={h} fill="url(#anv-studio-grid)" opacity="0.5" />
  </g>
);

// ─── Registry ────────────────────────────────────────────────────────────────
const STYLE_SPECS: Record<VisualStyle, StyleSpec> = {
  studio: {
    id: "studio",
    label: "Studio",
    tier: "filter",
    renderDefs: studioDefs,
    renderBackground: studioBg,
    mainFilterId: null,
    // accentColor omitted → templates keep their own colors (current v1 look)
  },
  whiteboard: {
    id: "whiteboard",
    label: "Whiteboard",
    tier: "filter",
    renderDefs: whiteboardDefs,
    renderBackground: whiteboardBg,
    mainFilterId: "anv-whiteboard-ink",
    fontFamily: '"Caveat", "Shantell Sans", "Comic Sans MS", system-ui, sans-serif',
  },
  watercolour: {
    id: "watercolour",
    label: "Watercolour",
    tier: "filter",
    renderDefs: watercolourDefs,
    renderBackground: watercolourBg,
    mainFilterId: "anv-watercolour-wash",
  },
  retro: {
    id: "retro",
    label: "Retro print",
    tier: "filter",
    renderDefs: retroDefs,
    renderBackground: retroBg,
    mainFilterId: "anv-retro-print",
    fontFamily: '"Archivo", "Oswald", "Arial Narrow", system-ui, sans-serif',
  },
  // Character-tier styles are not yet renderable (Phase 5). They fall back to
  // studio so the renderer never crashes; the picker keeps them locked.
  kawaii: {
    id: "kawaii",
    label: "Kawaii",
    tier: "character",
    renderDefs: studioDefs,
    renderBackground: studioBg,
    mainFilterId: null,
  },
  anime: {
    id: "anime",
    label: "Anime",
    tier: "character",
    renderDefs: studioDefs,
    renderBackground: studioBg,
    mainFilterId: null,
  },
};

export function getStyleSpec(id: VisualStyle | undefined | null): StyleSpec {
  if (id && STYLE_SPECS[id]) return STYLE_SPECS[id];
  return STYLE_SPECS[DEFAULT_VISUAL_STYLE];
}
